// src/pages/components/DialogDetalheAusencias.js

import React, { useEffect, useMemo, useState } from "react";

import * as XLSX from "xlsx-js-style";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import {
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";

import TableViewIcon from "@mui/icons-material/TableView";

const fmtNum = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString("pt-PT", { maximumFractionDigits: 1 });
};

const fmtDate = (d) => {
  if (!d) return "";

  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";

  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yy = dt.getFullYear();

  return `${dd}/${mm}/${yy}`;
};

const tipoDia = (a) => {
  const fr = Number(a?.fracao || 0);

  if (fr === 1) {
    const p = Number(a?.periodo || 0);

    if (p === 1) return "Meio dia (manhã)";
    if (p === 2) return "Meio dia (tarde)";

    return "Meio dia";
  }

  return "Dia inteiro";
};

const estadoTxt = (r) => {
  const v = Number(r?.estado);
  const cancel = Number(r?.cancelado ?? 0) === 1;

  if (v === 1 && cancel) return "Cancelado";
  if (v === 1) return "Validado";
  if (v === 2) return "Recusado";

  return "Pendente";
};

const isFeriasAnosAnteriores = (r) =>
  String(r?.motivo ?? "")
    .trim()
    .toUpperCase() === "FÉRIAS DE ANOS ANTERIORES";

export default function DialogDetalheAusencias({
  open,
  onClose,
  ano,
  idUtilizador,
  nomeUtilizador,
}) {
  const { showAlert } = useAlert();
  const { userData } = useAuth();

  const [detRows, setDetRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(null);

  useEffect(() => {
    if (!open) return;

    const carregar = async () => {
      if (!idUtilizador || !ano) {
        setDetRows([]);
        return;
      }

      setLoading(true);
      setDetRows([]);

      const body = {
        ano: Number(ano),
        idutilizador: Number(idUtilizador),
        idutilizador_solicitante: userData?.id,
        funcoes: userData?.funcoes ?? [],
      };

      const [payload, err] = await handleApi(api.post("Ausencias/SelResumoAusenciasDetalhe", body));

      setLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setDetRows(Array.isArray(payload) ? payload : []);
    };

    carregar();
  }, [open, ano, idUtilizador, userData?.id, userData?.funcoes, showAlert]);

  useEffect(() => {
    if (open) return;

    setDetRows([]);
    setLoading(false);
    setTooltipOpen(null);
  }, [open]);

  const exportarExcel = () => {
    if (!detRows.length) {
      showAlert("Não existem dados para exportar.", "warning");
      return;
    }

    const dados = detRows.map((r) => ({
      Dia: fmtDate(r.dia),
      Tipo: tipoDia(r),
      Férias: Number(r?.ferias ?? 1) === 1 ? "Sim" : "Não",
      Estado: estadoTxt(r),
      Função: r.funcao ?? "",
      "Obs. pedido": r.motivo ?? "",
      "Obs. validação/recusa":
        Number(r?.cancelado ?? 0) === 1
          ? r.motivo_cancelamento ?? ""
          : Number(r?.estado) === 2
          ? r.motivo_rejeicao ?? ""
          : r.override_motivo ?? "",
    }));

    const resumo = [
      ["Funcionário", nomeUtilizador ?? ""],
      ["Ano", Number(ano)],
      [],
      ["Pendentes", resumoDet.pend],
      ["Recusados", resumoDet.rec],
      ["Cancelados", resumoDet.canc],
      ["Validados (gozados)", resumoDet.valGoz],
      ["Validados (por gozar)", resumoDet.valPor],
      ["Total Férias", resumoDet.totalFerias],
      [`Total Férias ${ano}`, resumoDet.totalFeriasAno],
      [],
    ];

    const ws = XLSX.utils.aoa_to_sheet(resumo);

    // Acrescenta a tabela depois do resumo.
    XLSX.utils.sheet_add_json(ws, dados, {
      origin: "A12",
    });

    // Largura das colunas
    ws["!cols"] = [
      { wch: 24 }, // Dia / títulos do resumo
      { wch: 20 }, // Tipo
      { wch: 10 }, // Férias
      { wch: 14 }, // Estado
      { wch: 24 }, // Função
      { wch: 40 }, // Obs. pedido
      { wch: 40 }, // Obs. validação/recusa
    ];

    // Cabeçalho a negrito
    // Título dos campos do resumo
    for (let row = 0; row <= 9; row += 1) {
      const cell = ws[XLSX.utils.encode_cell({ r: row, c: 0 })];

      if (cell) {
        cell.s = {
          font: { bold: true },
        };
      }
    }

    // Destacar Total Férias do ano
    for (let col = 0; col <= 1; col += 1) {
      const cell = ws[XLSX.utils.encode_cell({ r: 9, c: col })];

      if (cell) {
        cell.s = {
          ...(cell.s || {}),
          font: {
            ...(cell.s?.font || {}),
            bold: true,
          },
        };
      }
    }

    // Cabeçalho da tabela (linha 12 no Excel)
    const range = XLSX.utils.decode_range(ws["!ref"]);

    // Centrar horizontal e verticalmente todas as células
    for (let row = range.s.r; row <= range.e.r; row += 1) {
      for (let col = range.s.c; col <= range.e.c; col += 1) {
        const cell = ws[XLSX.utils.encode_cell({ r: row, c: col })];

        if (cell) {
          cell.s = {
            ...(cell.s || {}),
            alignment: {
              ...(cell.s?.alignment || {}),
              horizontal: "center",
              vertical: "center",
            },
          };
        }
      }
    }

    for (let col = range.s.c; col <= range.e.c; col += 1) {
      const cell = ws[XLSX.utils.encode_cell({ r: 11, c: col })];

      if (cell) {
        cell.s = {
          ...(cell.s || {}),
          font: {
            ...(cell.s?.font || {}),
            bold: true,
          },
          alignment: {
            ...(cell.s?.alignment || {}),
            horizontal: "center",
            vertical: "center",
          },
        };
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Ausências ${ano}`);

    const nomeSeguro = String(nomeUtilizador || "Funcionario")
      .trim()
      .replace(/[\\/:*?"<>|]/g, "")
      .replace(/\s+/g, "_");

    XLSX.writeFile(wb, `Ausencias_${nomeSeguro}_${ano}.xlsx`);
  };

  const resumoDet = useMemo(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const frac = (r) => (Number(r?.fracao) === 1 ? 0.5 : 1);

    const isPast = (r) => {
      const d = new Date(r?.dia);
      d.setHours(0, 0, 0, 0);
      return d < hoje;
    };

    let valGoz = 0;
    let valPor = 0;
    let pend = 0;
    let rec = 0;
    let canc = 0;
    let totalFerias = 0;
    let totalFeriasAno = 0;

    for (const r of detRows) {
      const est = Number(r?.estado || 0);
      const cancel = Number(r?.cancelado ?? 0) === 1;
      const ferias = Number(r?.ferias ?? 1) === 1;

      // Cancelados contam apenas para "Cancelados".
      if (cancel) {
        canc += frac(r);
        continue;
      }

      if (ferias) {
        totalFerias += frac(r);

        // Férias efetivamente pertencentes ao ano apresentado.
        // Exclui os dias marcados como férias transitadas de anos anteriores.
        if (!isFeriasAnosAnteriores(r)) {
          totalFeriasAno += frac(r);
        }
      }

      if (est === 1) {
        if (isPast(r)) valGoz += ferias ? frac(r) : 0;
        else valPor += ferias ? frac(r) : 0;
      } else if (est === 2) {
        rec += frac(r);
      } else {
        pend += frac(r);
      }
    }

    return {
      pend,
      rec,
      canc,
      valGoz,
      valPor,
      totalFerias,
      totalFeriasAno,
    };
  }, [detRows]);

  const SummaryItem = ({ id, label, value, tooltip }) => (
    <ClickAwayListener onClickAway={() => setTooltipOpen(null)}>
      <Tooltip
        arrow
        placement="top"
        open={tooltipOpen === id}
        onClose={() => setTooltipOpen(null)}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: "rgba(0,0,0,0.78)",
              color: "#fff",
              fontSize: 12,
              borderRadius: "8px",
              px: 1.2,
              py: 0.8,
              maxWidth: 320,
            },
          },
          arrow: { sx: { color: "rgba(0,0,0,0.78)" } },
        }}
        title={tooltip || ""}
      >
        <MDTypography
          style={{
            fontSize: 12,
            cursor: "pointer",
            userSelect: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 8px",
            borderRadius: 8,
            background: "rgba(0,0,0,0.04)",
          }}
          onClick={() => setTooltipOpen((prev) => (prev === id ? null : id))}
          title="Clique para ajuda"
        >
          <strong>{label}</strong> {value}
        </MDTypography>
      </Tooltip>
    </ClickAwayListener>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Detalhe de Ausências — {nomeUtilizador ?? ""}
        <span style={{ display: "block", fontSize: 12, fontWeight: 400, opacity: 0.8 }}>
          Ano {ano}
        </span>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
            <CircularProgress />
          </MDBox>
        ) : (
          <>
            <MDBox mb={2} display="flex" gap={1.5} flexWrap="wrap">
              <SummaryItem
                id="pend"
                label="Pendentes:"
                value={fmtNum(resumoDet.pend)}
                tooltip="Pedidos ainda não analisados por um gestor."
              />

              <SummaryItem
                id="rec"
                label="Recusados:"
                value={fmtNum(resumoDet.rec)}
                tooltip="Pedidos recusados pelo gestor (não contam para férias nem para ocupação após a decisão)."
              />

              <SummaryItem
                id="canc"
                label="Cancelados:"
                value={fmtNum(resumoDet.canc)}
                tooltip="Pedidos que chegaram a ser validados, mas foram cancelados com motivo. Estes dias ficam novamente disponíveis."
              />

              <SummaryItem
                id="valGoz"
                label="Validados (gozados):"
                value={fmtNum(resumoDet.valGoz)}
                tooltip="Pedidos validados cuja data já passou. Só contam para férias se 'Férias = Sim'."
              />

              <SummaryItem
                id="valPor"
                label="Validados (por gozar):"
                value={fmtNum(resumoDet.valPor)}
                tooltip="Pedidos validados cuja data ainda não chegou. Só contam para férias se 'Férias = Sim'."
              />

              <SummaryItem
                id="totalFerias"
                label="Total Férias:"
                value={fmtNum(resumoDet.totalFerias)}
                tooltip="Soma dos dias que contam para férias, incluindo férias de anos anteriores."
              />

              <SummaryItem
                id="totalFeriasAno"
                label={`Total Férias ${ano}:`}
                value={fmtNum(resumoDet.totalFeriasAno)}
                tooltip={`Férias pertencentes a ${ano}, excluindo férias de anos anteriores.`}
              />
            </MDBox>

            <MDBox style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {[
                      "Dia",
                      "Tipo",
                      "Férias",
                      "Estado",
                      "Função",
                      "Obs. pedido",
                      "Obs. validação/recusa",
                    ].map((h) => (
                      <th
                        key={h}
                        style={{
                          fontSize: 12,
                          textAlign: "left",
                          padding: "8px 6px",
                          borderBottom: "1px solid rgba(0,0,0,0.15)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {detRows.map((r) => (
                    <tr
                      key={r.id}
                      style={{
                        borderBottom: "1px solid rgba(0,0,0,0.06)",
                        textDecoration: Number(r?.cancelado ?? 0) === 1 ? "line-through" : "none",
                        opacity: Number(r?.cancelado ?? 0) === 1 ? 0.7 : 1,
                      }}
                    >
                      <td
                        style={{
                          fontSize: 12,
                          padding: "8px 6px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fmtDate(r.dia)}
                      </td>

                      <td
                        style={{
                          fontSize: 12,
                          padding: "8px 6px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {tipoDia(r)}
                      </td>

                      <td
                        style={{
                          fontSize: 12,
                          padding: "8px 6px",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                        }}
                      >
                        {Number(r?.ferias ?? 1) === 1 ? "Sim" : "Não"}
                      </td>

                      <td
                        style={{
                          fontSize: 12,
                          padding: "8px 6px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {estadoTxt(r)}
                      </td>

                      <td
                        style={{
                          fontSize: 12,
                          padding: "8px 6px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {r.funcao ?? ""}
                      </td>

                      <td style={{ fontSize: 12, padding: "8px 6px" }}>{r.motivo ?? ""}</td>

                      <td style={{ fontSize: 12, padding: "8px 6px" }}>
                        {Number(r?.cancelado ?? 0) === 1
                          ? r.motivo_cancelamento ?? ""
                          : r.estado === 2
                          ? r.motivo_rejeicao ?? ""
                          : r.override_motivo ?? ""}
                      </td>
                    </tr>
                  ))}

                  {detRows.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ padding: 12, fontSize: 12, color: "#667" }}>
                        Sem registos para o ano selecionado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </MDBox>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "space-between",
          px: 2,
          py: 1,
        }}
      >
        <MDButton
          color="success"
          size="small"
          startIcon={<TableViewIcon />}
          onClick={exportarExcel}
          disabled={loading || detRows.length === 0}
        >
          Exportar Excel
        </MDButton>

        <MDButton color="secondary" variant="outlined" onClick={onClose}>
          Fechar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
